/**
 * Componente VatLookupButton
 * Pulsante per cercare informazioni aziendali da P.IVA
 * Si integra nei form di onboarding e impostazioni azienda
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './dialog';
import { toast } from 'sonner';
import {
  Search,
  Loader2,
  CheckCircle,
  XCircle,
  Building2,
  MapPin,
  Mail,
  FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export interface VatLookupData {
  vat_code: string;
  company_name: string;
  tax_code?: string;
  legal_form?: string;
  address?: string;
  street?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  region?: string;
  country?: string;
  pec?: string;
  sdi_code?: string;
  phone?: string;
  email?: string;
  ateco_code?: string;
  ateco_description?: string;
}

interface VatLookupButtonProps {
  onDataFound: (data: VatLookupData) => void;
  disabled?: boolean;
  vatValue?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
}

export const VatLookupButton: React.FC<VatLookupButtonProps> = ({
  onDataFound,
  disabled = false,
  vatValue = '',
  variant = 'outline',
  size = 'default',
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const [vatCode, setVatCode] = useState(vatValue);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VatLookupData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serviceAvailable, setServiceAvailable] = useState<boolean | null>(null);

  // Check if service is available when dialog opens
  const handleOpen = async () => {
    setVatCode(vatValue);
    setResult(null);
    setError(null);
    setOpen(true);
    
    // Check service status
    try {
      const res = await axios.get(`${API}/vat-lookup/check/status`);
      setServiceAvailable(res.data.enabled);
      if (!res.data.enabled) {
        setError(res.data.reason || 'Servizio non disponibile');
      }
    } catch (err) {
      setServiceAvailable(false);
      setError('Servizio non disponibile');
    }
  };

  const handleSearch = async () => {
    if (!vatCode) {
      setError('Inserisci una P.IVA');
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const res = await axios.get(`${API}/vat-lookup/${vatCode}`);
      if (res.data.success) {
        setResult(res.data.data);
      } else {
        setError(res.data.error || 'Nessun risultato trovato');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (result) {
      onDataFound(result);
      toast.success('Dati aziendali applicati!');
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={handleOpen}
        disabled={disabled}
        className={className}
        data-testid="vat-lookup-btn"
      >
        <Search className="h-4 w-4 mr-2" />
        Cerca da P.IVA
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="vat-lookup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Ricerca Azienda da P.IVA
            </DialogTitle>
            <DialogDescription>
              Inserisci la Partita IVA per recuperare automaticamente i dati aziendali
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Input P.IVA */}
            <div className="space-y-2">
              <Label>Partita IVA</Label>
              <div className="flex gap-2">
                <Input
                  value={vatCode}
                  onChange={(e) => setVatCode(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  placeholder="Es: 01234567890"
                  maxLength={11}
                  className="font-mono"
                  disabled={!serviceAvailable}
                  data-testid="vat-lookup-input"
                />
                <Button
                  onClick={handleSearch}
                  disabled={loading || !vatCode || vatCode.length !== 11 || !serviceAvailable}
                  data-testid="vat-lookup-search-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Inserisci le 11 cifre della P.IVA italiana
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
                <XCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Result */}
            {result && (
              <div className="space-y-3 p-4 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="h-4 w-4" />
                  Azienda trovata
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">{result.company_name}</p>
                      {result.legal_form && (
                        <p className="text-muted-foreground">{result.legal_form}</p>
                      )}
                    </div>
                  </div>
                  
                  {result.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <p>{result.address}</p>
                    </div>
                  )}
                  
                  {result.pec && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <p>{result.pec}</p>
                    </div>
                  )}
                  
                  {result.sdi_code && (
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <p>Codice SDI: {result.sdi_code}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            {result && (
              <Button onClick={handleApply} data-testid="vat-lookup-apply-btn">
                <CheckCircle className="h-4 w-4 mr-2" />
                Usa questi dati
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VatLookupButton;
